/**
 * The chat composer — RN counterpart of the web widget's `message-input.tsx`.
 *
 * Web drives the field with react-hook-form + zod. Neither survives the
 * crossing and neither is missed: there is no form element to submit and no
 * browser validation to suppress, so the draft is one piece of local UI state
 * and the whole of the schema (`z.string().min(1)`) is the length check in
 * `submitDraft`.
 *
 * The web pill is frosted glass — 60% background plus `backdrop-filter: blur`
 * over the scrolling transcript. RN has no backdrop filter on either platform,
 * so the pill is painted solid in `theme.backgroundColor`: the exact colour web
 * itself falls back to whenever the command menu is open.
 *
 * The field is multiline here because a phone keyboard has no horizontal
 * scroll worth typing into; it grows with the text up to a cap and only then
 * scrolls internally.
 */

import { type FC, useCallback, useMemo, useState } from "react";
import { StyleSheet, TextInput, View, type TextInputContentSizeChangeEvent } from "react-native";
import { ArrowUp } from "lucide-react-native";

import { GradientFill, solidColor } from "../lib/gradient";
import { filterSlashCommands, type SlashCommand } from "../lib/slash-command-types";
import { ringgId } from "../lib/testing";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";
import SlashCommandMenu from "./slash-command-menu";
import { Touchable } from "./ui/pressable";

interface MessageInputProps {
  isSending?: boolean;
  enabledSlashCommands?: SlashCommand[];
  onMessageSubmit?: (message: string) => void;
  onSlashCommand?: (command: SlashCommand) => void;
}

/** `text-[15px]`. */
const INPUT_FONT_SIZE = 15;

/**
 * Web pairs `text-[15px]` with no leading class, so the line box is whatever
 * the platform renders. 20 is the working figure for this size and is used
 * only to place the single line and to size the growth cap.
 */
const INPUT_LINE_HEIGHT = 20;

/** `h-12` — the collapsed pill: one line plus its padding. */
const MIN_INPUT_HEIGHT = 48;

/** Beyond four lines the field keeps its height and scrolls its own content. */
const MAX_INPUT_LINES = 4;
const MAX_INPUT_HEIGHT = MIN_INPUT_HEIGHT + (MAX_INPUT_LINES - 1) * INPUT_LINE_HEIGHT;

/** Centres the single line inside the collapsed pill. */
const INPUT_VERTICAL_PADDING = (MIN_INPUT_HEIGHT - INPUT_LINE_HEIGHT) / 2;

/** The container's two shapes: `rounded-full` closed, `rounded-xl` with the menu open. */
const PILL_RADIUS = 9999;
const MENU_OPEN_RADIUS = 12;

const SEND_BUTTON_SIZE = 32;

/**
 * Both platforms report the measured text height with the field's own padding
 * included, so the number is directly comparable to the pill height and the
 * clamp needs no per-platform correction.
 */
const clampInputHeight = (height: number): number => Math.min(Math.max(height, MIN_INPUT_HEIGHT), MAX_INPUT_HEIGHT);

const MessageInput: FC<MessageInputProps> = ({ isSending, enabledSlashCommands, onMessageSubmit, onSlashCommand }) => {
  const theme = useWidgetTheme();
  const [message, setMessage] = useState("");
  const [activeCommandIndex, setActiveCommandIndex] = useState(0);
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_HEIGHT);

  const isSlashQuery = message.startsWith("/");
  const availableCommands = enabledSlashCommands ?? [];

  const filteredCommands = useMemo(() => {
    if (!isSlashQuery || availableCommands.length === 0) return [];
    return filterSlashCommands(availableCommands, message);
  }, [isSlashQuery, availableCommands, message]);

  const showMenu = isSlashQuery && filteredCommands.length > 0;

  const clearDraft = useCallback(() => {
    setMessage("");
    setActiveCommandIndex(0);
    // Clearing the value does re-measure the field, but not before the next
    // frame; resetting the height here keeps the pill from staying tall.
    setInputHeight(MIN_INPUT_HEIGHT);
  }, []);

  const handleSelectCommand = useCallback(
    (command: SlashCommand) => {
      // Web writes the command into the field and resets the form on the next
      // line, so an empty draft is the only observable end state.
      if (onSlashCommand) {
        onSlashCommand(command);
      } else {
        onMessageSubmit?.(command.command);
      }
      clearDraft();
    },
    [clearDraft, onMessageSubmit, onSlashCommand],
  );

  const submitDraft = useCallback(() => {
    // Web's zod schema is `min(1)` and nothing else: whitespace is a message
    // there, an empty string is silently dropped with no error under the pill.
    if (message.length === 0) return;
    onMessageSubmit?.(message);
    clearDraft();
  }, [clearDraft, message, onMessageSubmit]);

  const handleSubmitEditing = useCallback(() => {
    // Return carries the same intent as web's Enter: take the highlighted
    // command while the menu is open, otherwise send the draft. The rest of
    // web's key handling (ArrowUp/ArrowDown to move, Tab to accept, Escape to
    // clear) has no counterpart on a soft keyboard and is dropped — the list
    // is navigated by tapping it.
    if (!showMenu) {
      submitDraft();
      return;
    }
    // noUncheckedIndexedAccess guard — showMenu guarantees a non-empty list
    // and the index is reset on every keystroke, so this always hits.
    const command = filteredCommands[activeCommandIndex];
    if (command) handleSelectCommand(command);
  }, [activeCommandIndex, filteredCommands, handleSelectCommand, showMenu, submitDraft]);

  const handleChangeText = useCallback((value: string) => {
    setMessage(value);
    setActiveCommandIndex(0);
  }, []);

  const handleContentSizeChange = useCallback((event: TextInputContentSizeChangeEvent) => {
    setInputHeight(clampInputHeight(event.nativeEvent.contentSize.height));
  }, []);

  const borderColor = solidColor(theme.borderColor);

  return (
    <View testID={ringgId("message-input-form")}>
      {/* Single unified container: command list on top, input on bottom. */}
      <View
        testID={ringgId("message-input")}
        style={[styles.container, { backgroundColor: theme.backgroundColor, borderColor, borderRadius: showMenu ? MENU_OPEN_RADIUS : PILL_RADIUS }]}
      >
        {showMenu ? <SlashCommandMenu commands={filteredCommands} activeIndex={activeCommandIndex} theme={theme} onSelect={handleSelectCommand} onActiveIndexChange={setActiveCommandIndex} /> : null}
        {/* Web floats the send button over a full-width field; a flex row lands
            it in the same place without absolute positioning, and keeps it on
            the last line rather than mid-pill once the field has grown. */}
        <View testID={ringgId("message-input-row")} style={[styles.row, showMenu ? { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: borderColor } : null]}>
          <TextInput
            testID={ringgId("message-input-field")}
            value={message}
            onChangeText={handleChangeText}
            onContentSizeChange={handleContentSizeChange}
            onSubmitEditing={handleSubmitEditing}
            placeholder={availableCommands.length > 0 ? "Type /command..." : "Type here..."}
            // Web leaves the placeholder to the browser's own grey; RN draws
            // nothing unless it is given a colour.
            placeholderTextColor={theme.mutedTextColor}
            autoComplete="off"
            multiline
            returnKeyType="send"
            // A multiline field treats Return as a newline unless told
            // otherwise, and the deprecated `blurOnSubmit={false}` suppresses
            // the submit event entirely. "submit" is the one setting that both
            // fires onSubmitEditing and leaves the keyboard up between messages.
            submitBehavior="submit"
            // Android decorates a bare input with an underline; the pill's own
            // border is the only edge this design has.
            underlineColorAndroid="transparent"
            style={[styles.input, { height: inputHeight, color: theme.textColor, fontFamily: resolveFontFamily(theme.fontFamily) }]}
          />
          <Touchable onPress={submitDraft} disabled={isSending} testID={ringgId("message-send-button")} accessibilityLabel="Send message" style={styles.sendButton}>
            <GradientFill color={theme.primaryColor} style={styles.sendButtonSurface}>
              <ArrowUp size={16} color={theme.primaryTextColor} />
            </GradientFill>
          </Touchable>
        </View>
      </View>
    </View>
  );
};

export default MessageInput;

const styles = StyleSheet.create({
  container: { borderWidth: StyleSheet.hairlineWidth, overflow: "hidden" },
  row: { flexDirection: "row", alignItems: "flex-end" },
  input: {
    flex: 1,
    fontSize: INPUT_FONT_SIZE,
    paddingLeft: 20,
    // 12 here plus the button's 32 and its 4 of margin reproduce web's `pr-12`.
    paddingRight: 12,
    paddingVertical: INPUT_VERTICAL_PADDING,
    // Text grows downward from the top edge instead of re-centring on Android.
    textAlignVertical: "top",
  },
  sendButton: { marginRight: 4, marginBottom: (MIN_INPUT_HEIGHT - SEND_BUTTON_SIZE) / 2 },
  sendButtonSurface: { width: SEND_BUTTON_SIZE, height: SEND_BUTTON_SIZE, borderRadius: PILL_RADIUS, alignItems: "center", justifyContent: "center" },
});
